import { motion } from 'framer-motion';
import React from 'react';
import QuizQuestionTimer from './QuizQuestionTimer';
import CheckIndicator from '../CheckIndicator';

export default function StandardQuestion({
  message,
  question,
  selectMany = false,
  endedEarly = false,
  reportAnswer,
  isLocked,
  setIsLocked,
  content = JunoContent || null,
}) {
  const { Answers, Description } = question;
  const {
    timetoken,
    message: {
      data: { time },
    },
  } = message;

  const [selectedAnswers, setSelectedAnswers] = React.useState([]);
  const [answerResults, setAnswerResults] = React.useState([]);

  const onSelectAnswer = answer => {
    if (CrowdHub_App.INSTANCE.CurrentUser.IsLoggedIn() == false) {
      CrowdHub_App.INSTANCE.requireLogin(function () {});
      return;
    }

    const pollAction = content?.GetActionById(CrowdHub_App.ACTION_POLL);
    if (pollAction && question.TypeName === 'Poll') {
      pollAction.Complete(content?.Id, () => {}, null, false);
    }

    // remove the answer if it was already selected, add it if it wasnt
    if (selectedAnswers.includes(answer.answer)) {
      setSelectedAnswers([]);
    } else {
      setSelectedAnswers([answer.answer_id]);
    }
  };

  const onSelectMultiAnswer = answer => {
    if (CrowdHub_App.INSTANCE.CurrentUser.IsLoggedIn() == false) {
      CrowdHub_App.INSTANCE.requireLogin(function () {});
      return;
    }

    // remove the answer if it was already selected, add it if it wasnt
    if (selectedAnswers.includes(answer.answer_id)) {
      setSelectedAnswers(old => old.filter(answer_id => answer_id != answer.answer_id));
    } else {
      setSelectedAnswers(old => [...old, answer.answer_id]);
    }
  };

  React.useEffect(() => {
    if (selectedAnswers.length > 0 && !isLocked) {
      CrowdHub_App.INSTANCE.mServices.SyncQuizQuestionAnswers(
        question.Id,
        selectedAnswers,
        response => {
          if (CrowdHub_App.CONFIG.VERBOSE_GENERAL_LOGGING) {
            console.log(response);
          }

          // map over the answers and report them to the activity log
          selectedAnswers.map(selectedAnswer => {
            reportAnswer(
              { mQuestion: question, mConstructorParams: { question: question } },
              Answers.find(answer => answer.answer_id == selectedAnswer),
            );
          });
        },
        error => {
          console.error(error);
        },
        parseInt(message.timetoken.slice(0, 10)),
      );
    }
  }, [selectedAnswers]);

  const onQuestionEnd = () => {
    setIsLocked(true);

    // give it a second in case people's timers are off by a bit
    setTimeout(() => {
      CrowdHub_App.INSTANCE.mServices.GetQuizQuestion(
        question.Id,
        function (aQuestionResponse) {
          setAnswerResults(getAnswerPercent(aQuestionResponse));
        },
        function () {},
        parseInt(message.timetoken.slice(0, 10)),
      );
    }, 1000);
  };

  const onQuestionAlreadyEnded = () => {
    setIsLocked(true);
    // give it a second in case people's timers are off by a bit
    setTimeout(() => {
      CrowdHub_App.INSTANCE.mServices.GetQuizQuestion(
        question.Id,
        function (aQuestionResponse) {
          setAnswerResults(getAnswerPercent(aQuestionResponse));
          setSelectedAnswers(
            aQuestionResponse.Answers.filter(answer => answer.user_answered === 'true').map(answer => answer.answer_id),
          );
        },
        function () {},
        parseInt(message.timetoken.slice(0, 10)),
      );
    }, 1000);
  };

  // get % of people that responded with each answer
  const getAnswerPercent = arr => {
    const answers = arr.Answers.map(answer => parseInt(answer.answer_total_answers));
    const totalAnswers = answers.reduce((partialSum, a) => partialSum + a, 0);
    const answerPercentages = answers.map(answer => {
      return answer / totalAnswers ? answer / totalAnswers : 0;
    });
    return answerPercentages;
  };

  const selectedBlobVariants = {
    visible: i => ({
      width: `${answerResults[i] * 100}%`,
      left: '0',
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 40,
        duration: 0.2,
      },
    }),
    hidden: {
      width: '0%',
      left: '0',
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 40,
        duration: 0.2,
      },
    },
  };

  return (
    <div
      className={`question_question question_container question_message`}
      id={`${App.INSTANCE.CurrentUser.Id}${message.timetoken}`}
    >
      <div className={`question ${isLocked ? 'locked' : ''}`}>
        <div className={`description`} dangerouslySetInnerHTML={{ __html: Description }} />
        <motion.div className={`answers`} variants={answersVariants} initial={`hidden`} animate={`show`}>
          {Answers.map((answer, i) => {
            return (
              <motion.div
                role={`button`}
                className={`cssButton answer_button ${selectedAnswers.includes(answer.answer_id) ? ' selected' : ''} ${
                  isLocked && answer.answer_correct == 'true' ? ' correct' : ''
                }`}
                key={`answer_${answer.answer_id}`}
                value={answer.answer_id}
                onClick={e => {
                  selectMany ? onSelectMultiAnswer(answer) : onSelectAnswer(answer);
                }}
                variants={answerVariants}
              >
                <CheckIndicator isChecked={selectedAnswers.includes(answer.answer_id)} />
                <div className={`answer_text`}>{answer.answer_answer}</div>
                {answerResults.length > 0 ? <div className="percent">{Math.round(answerResults[i] * 100)}%</div> : ''}
                <motion.div
                  className={`selectedBlob`}
                  custom={i}
                  variants={selectedBlobVariants}
                  initial={'hidden'}
                  animate={answerResults.length > 0 ? 'visible' : 'hidden'}
                ></motion.div>
              </motion.div>
            );
          })}
        </motion.div>
        <QuizQuestionTimer
          questionTime={timetoken}
          questionDuration={time}
          onQuestionEnd={onQuestionEnd}
          onQuestionAlreadyEnded={onQuestionAlreadyEnded}
          endedEarly={endedEarly}
        />
      </div>
    </div>
  );
}

const answersVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.4,
    },
  },
};

const answerVariants = {
  hidden: { opacity: 0, x: 50 },
  show: { opacity: 1, x: 0 },
};
